import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { REPORT_GROUPS, listedReports } from "../definitions";

// Title (which doubles as the report switcher), what the report covers and
// when it was generated, and the window controls.
export const ReportHeader = ({
  definition,
  subtitle,
  popout,
  onBack,
  onSwitch,
  onPopout,
  children,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const title = definition?.title ?? "Reports";

  return (
    <Box
      component="header"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: { xs: 2, md: 3 },
        pt: 2,
        pb: 1,
        minWidth: 0,
      }}
    >
      {!popout && (
        <Tooltip title="Back to reports">
          <IconButton aria-label="Back to reports" onClick={onBack} color="primary">
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Button
          aria-haspopup="menu"
          aria-label={`Report: ${title}. Choose another report`}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          endIcon={<ArrowDropDownIcon />}
          sx={{
            textTransform: "none",
            fontSize: "1.25rem",
            fontWeight: 500,
            lineHeight: 1.3,
            px: 1,
            ml: -1,
            maxWidth: "100%",
            justifyContent: "flex-start",
            "& .MuiButton-endIcon": { ml: 0.5 },
          }}
        >
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {title}
          </Box>
        </Button>
        {subtitle && (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="report-subtitle"
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {subtitle}
          </Typography>
        )}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          {Object.entries(REPORT_GROUPS).flatMap(([group, label]) => [
            <ListSubheader key={group}>{label}</ListSubheader>,
            ...listedReports(group).map((d) => (
              <MenuItem
                key={d.slug}
                selected={d.slug === definition?.slug}
                disabled={Boolean(d.comingSoon)}
                onClick={() => {
                  setMenuAnchor(null);
                  onSwitch(d.slug);
                }}
              >
                {d.comingSoon ? `${d.title} (coming soon)` : d.title}
              </MenuItem>
            )),
          ])}
        </Menu>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
        {children}
        {!popout && (
          <Tooltip title="Open in a new window">
            <IconButton aria-label="Open in a new window" onClick={onPopout} color="primary">
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};
